'use client'

import { useRouteContext } from '@/app/layouts/TenantLayout'
import { isRootProject } from '@/components/organization/organizationUtils'
import { getDefaultOrganizationId, getUserPinnedProjectId, isPlatformUser } from '@/stores/auth'
import {
	useAddRecentTenant,
	useAuthStore,
	useCurrentOrganizationIdFromState,
	useCurrentTenant,
	useSwitchTenant,
} from '@/stores/authStore'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'

export interface OrganizationItem {
	id: string
	name: string
	planName?: string
	isOwner: boolean
	tenants: { id: string; name: string }[]
}

export function useOrganizations(searchTerm = '') {
	const user = useAuthStore(s => s.user)
	const email = user?.email

	const organizations = useMemo<OrganizationItem[]>(() => {
		if (!user) {
			return []
		}

		const orgMap = new Map<string, OrganizationItem>()

		if (user.organizations) {
			for (const org of user.organizations) {
				orgMap.set(org.id, {
					id: org.id,
					name: org.name,
					planName: org.subscription?.plan_name,
					isOwner: email ? (org.owners?.includes(email) ?? false) : false,
					tenants: [],
				})
			}
		}

		if (isPlatformUser(user) && user.tenant_list) {
			const tenants = user.tenant_list
			for (const tenant of tenants) {
				const org = tenant.organization
				if (!org) {
					continue
				}

				if (!orgMap.has(org.id)) {
					orgMap.set(org.id, {
						id: org.id,
						name: org.name,
						planName: org.subscription?.plan_name,
						isOwner: email ? (org.owners?.includes(email) ?? false) : false,
						tenants: [],
					})
				}

				if (isRootProject(tenant.id, tenants)) {
					continue
				}

				const orgItem = orgMap.get(org.id)
				if (orgItem && !orgItem.tenants.find(t => t.id === tenant.id)) {
					orgItem.tenants.push({ id: tenant.id, name: tenant.name || tenant.id })
				}
			}
		}

		return Array.from(orgMap.values())
	}, [user, email])

	const filteredOrganizations = useMemo(() => {
		if (!searchTerm) {
			return organizations
		}
		const lowerTerm = searchTerm.toLowerCase()
		return organizations.filter(
			org =>
				org.name.toLowerCase().includes(lowerTerm) || org.tenants.some(t => t.name.toLowerCase().includes(lowerTerm)),
		)
	}, [organizations, searchTerm])

	return {
		organizations,
		filteredOrganizations,
		isEmpty: organizations.length === 0,
		isFiltered: searchTerm.length > 0,
	}
}

export function useProjects({
	searchTerm = '',
	orgIdOverride,
}: { searchTerm?: string; orgIdOverride?: string | null } = {}) {
	const tenantList = useAuthStore(s => (s.user && isPlatformUser(s.user) ? s.user.tenant_list : null))
	const orgIdFromState = useCurrentOrganizationIdFromState()
	const currentOrgId = orgIdOverride ?? orgIdFromState
	const tenants = useMemo(() => {
		if (!tenantList || !currentOrgId) {
			return []
		}

		// Same exclusions as getAccessibleTenants, so the switcher never names a project the
		// navigation fallbacks refuse to switch into.
		return tenantList
			.filter(t => t.org_id === currentOrgId && !isRootProject(t.id, tenantList) && t.deactivated_at == null)
			.map(t => ({ id: t.id, name: t.name || t.id }))
	}, [tenantList, currentOrgId])

	const filteredTenants = useMemo(() => {
		if (!searchTerm) {
			return tenants
		}
		const lowerTerm = searchTerm.toLowerCase()
		return tenants.filter(
			tenant => tenant.name.toLowerCase().includes(lowerTerm) || tenant.id.toLowerCase().includes(lowerTerm),
		)
	}, [tenants, searchTerm])

	return {
		tenants,
		filteredTenants,
		isEmpty: tenants.length === 0,
		isFiltered: searchTerm.length > 0,
		isLoading: false,
	}
}

export function useProjectSwitch({ onProjectSelect }: { onProjectSelect?: (tenantId: string) => void } = {}) {
	const { tenantId: routeTenantId } = useRouteContext()
	const currentTenant = useCurrentTenant()
	const switchTenant = useSwitchTenant()
	const addRecentTenant = useAddRecentTenant()
	const navigate = useNavigate()
	const tenantList = useAuthStore(s => (s.user && isPlatformUser(s.user) ? s.user.tenant_list : null))

	const handleProjectSwitch = useCallback(
		async (tenantId: string, callbacks?: { onSuccess?: () => void }) => {
			// Skip only when URL already shows this project; org-level URLs still need navigation.
			if (routeTenantId !== undefined && tenantId === routeTenantId) {
				callbacks?.onSuccess?.()
				return
			}

			let tenantName = tenantId
			if (tenantList) {
				const tenant = tenantList.find(t => t.id === tenantId)
				if (tenant) {
					tenantName = tenant.name || tenant.id
				}
			}

			toast.info(`Switching to project "${tenantName}"`)
			addRecentTenant(tenantId)

			if (onProjectSelect) {
				onProjectSelect(tenantId)
				callbacks?.onSuccess?.()
			} else {
				setTimeout(() => {
					switchTenant(tenantId, navigate)
					callbacks?.onSuccess?.()
				}, 200)
			}
		},
		[routeTenantId, addRecentTenant, onProjectSelect, switchTenant, navigate, tenantList],
	)

	return {
		currentTenant,
		handleProjectSwitch,
	}
}

export function useDefaultProject(orgId: string | null) {
	const user = useAuthStore(s => s.user)

	return useMemo(() => {
		if (!orgId || !user || !isPlatformUser(user)) {
			return null
		}
		return getUserPinnedProjectId(user, orgId)
	}, [user, orgId])
}

export function useSetDefaultProject() {
	const [isPinning, setIsPinning] = useState(false)

	const setDefault = useCallback(async (orgId: string, tenantId: string | null) => {
		setIsPinning(true)
		try {
			const { user, updateUserSettings } = useAuthStore.getState()
			if (!user || !isPlatformUser(user)) {
				toast.error('Pinning is not available for this account')
				return
			}

			// Personal pin in user settings — works for members and owners alike.
			const defaultProjects = { ...(user.settings?.default_projects ?? {}) }
			if (tenantId) {
				defaultProjects[orgId] = tenantId
			} else {
				delete defaultProjects[orgId]
			}
			await updateUserSettings({ default_projects: defaultProjects })

			toast.success(tenantId ? 'Default project set' : 'Default project cleared')
		} catch (error) {
			console.error('Failed to update default project:', error)
			toast.error('Failed to update default project')
		} finally {
			setIsPinning(false)
		}
	}, [])

	return { setDefault, isPinning }
}

export function useDefaultOrganization() {
	const user = useAuthStore(s => s.user)
	return useMemo(() => getDefaultOrganizationId(user), [user])
}

export function useSetDefaultOrganization() {
	const [isPinning, setIsPinning] = useState(false)

	const setDefault = useCallback(async (orgId: string | null) => {
		setIsPinning(true)
		try {
			const { user, updateUserSettings } = useAuthStore.getState()
			if (!user || !isPlatformUser(user)) {
				toast.error('Pinning is not available for this account')
				return
			}

			// Personal pin in user settings — works for members and owners alike.
			await updateUserSettings({ default_organization: orgId ?? undefined })

			toast.success(orgId ? 'Default organization set' : 'Default organization cleared')
		} catch (error) {
			console.error('Failed to update default organization:', error)
			toast.error('Failed to update default organization')
		} finally {
			setIsPinning(false)
		}
	}, [])

	return { setDefault, isPinning }
}

export function useCurrentOrgInfo(currentOrgId: string | null) {
	const user = useAuthStore(s => s.user)

	return useMemo(() => {
		if (!currentOrgId || !user) {
			return { name: 'Select Organization', planName: undefined }
		}

		if (user.organizations) {
			const org = user.organizations.find(o => o.id === currentOrgId)
			if (org) {
				return {
					name: org.name,
					planName: org.subscription?.plan_name,
				}
			}
		}

		if (isPlatformUser(user) && user.tenant_list) {
			const tenant = user.tenant_list.find(t => t.org_id === currentOrgId)
			if (tenant?.organization) {
				return {
					name: tenant.organization.name,
					planName: tenant.organization.subscription?.plan_name,
				}
			}
		}

		return { name: currentOrgId, planName: undefined }
	}, [user, currentOrgId])
}
